import type { DaoError, IDataSource } from "@/data/dao/service/BaseServiceDao";
import type { IPaginatedQry } from "@/data/queries/PaginatedQry";
import { CurrentLocationService } from "@/data/services/CurrentLocationService";
import { Format } from "@/data/util/Format";

import { isRequestAbortedError, logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";

export interface ITableFilterExtension {
    onChange?: { (value: any) };
}

export interface IFilterProviver {
    getFilter(): IPaginatedQry;
    setFilter(qry: IPaginatedQry);
}

export interface IRowClickEvent<T> {
    (model: T, e: JQueryEventObject | JQueryMouseEventObject);
}

export interface IColumnFormat<T> {
    (model: T, value: any): any;
}

export interface IColumnMapping<T> {
    name: string;
    property: string;
    styles?: object;
    sortable?: boolean;
    hideTablet?: boolean;
    hideMobile?: boolean;
    formatter?: IColumnFormat<T>;
}

export interface IDataTableConfig<T> {
    filterProvider?: IFilterProviver;
    rowsPerPage?: number;
    defaultSortProperty?: string;
    defaultSortAsc?: boolean;
    columnMapping: Array<IColumnMapping<T>>;
    autoload?: boolean;
    locationDependend?: boolean;
    paginated?: boolean;
    beforeTableClears?: { () };
    dataEvent?: { (values: Array<T>) };
    noDatatext?: string;
    rowClass?: { (v: T): string };
    useJsHistory?: boolean;
}

export class DataTable<T> {
    private clickEvent?: IRowClickEvent<T>;
    private mouseWheelClickEvent?: IRowClickEvent<T>;

    private $element: JQuery;
    private $table: JQuery;
    private $tHead: JQuery;
    private $tBody: JQuery;
    private $pageInfo: JQuery;
    private $pageinate?: JQuery;

    private config: IDataTableConfig<T>;
    private dataSource: IDataSource<T>;

    private currentPage = 0;
    private currentItems = 0;
    private hasNext?: boolean;
    private hasPrev?: boolean;

    private abortController?: AbortController;

    constructor(element: JQuery, dataSource: IDataSource<T>, config: IDataTableConfig<T>) {
        this.$element = element;
        this.config = config;

        if (this.config.autoload === undefined) this.config.autoload = true;

        if (this.config.rowsPerPage === undefined) this.config.rowsPerPage = 20;

        if (this.config.paginated === undefined) this.config.paginated = true;

        if (this.config.useJsHistory === undefined) this.config.useJsHistory = true;

        this.dataSource = dataSource;

        this.$table = $('<table class="kt-datatable__table" />').appendTo(this.$element);
        this.$tHead = $('<thead class="kt-datatable__head" />').appendTo(this.$table);
        this.$tBody = $('<tbody class="kt-datatable__body" />').appendTo(this.$table);

        const pageRow = $('<div class="kt-datatable__pager kt-datatable--paging-loaded" />').appendTo(this.$element);
        if (this.config.paginated !== false) {
            this.$pageinate = $('<ul class="kt-datatable__pager-nav">').appendTo(pageRow);
        }

        this.$pageInfo = $('<div class="kt-datatable__pager-info"></div>').appendTo(pageRow);

        // draw head
        if (this.config.useJsHistory) {
            const state = history.state;
            if (state !== undefined && state != null) {
                if (state.offset !== undefined) this.currentPage = Math.floor(state.offset / this.config.rowsPerPage);

                if (state.orderBy !== undefined) this.config.defaultSortProperty = state.orderBy;

                if (state.isAscending !== undefined) this.config.defaultSortAsc = state.isAscending;

                if (this.config.filterProvider !== undefined) this.config.filterProvider.setFilter(state);
            }
        }

        const headRow = $('<tr class="kt-datatable__row" />').appendTo(this.$tHead);
        for (const i in this.config.columnMapping) {
            const m = this.config.columnMapping[i];
            const th = $(
                `<th class="kt-datatable__cell kt-datatable__cell--sort" ${m.styles !== undefined ? `style="${this.resolveStyles(m.styles)}"` : ""}><span>${this.config.columnMapping[i].name}</span></th>`
            ).appendTo(headRow);

            if (m.hideMobile !== undefined && m.hideMobile === true) {
                th.addClass("kt-visible-desktop-table-cell kt-visible-tablet-table-cell");
            } else if (m.hideTablet !== undefined && m.hideTablet === true) {
                th.addClass("kt-visible-desktop-table-cell");
            }

            if (m.sortable === undefined || m.sortable !== false) {
                if (
                    this.config.defaultSortProperty !== undefined &&
                    this.config.defaultSortProperty === m["property"]
                ) {
                    th.addClass(
                        this.config.defaultSortAsc === undefined
                            ? "sorting_asc"
                            : this.config.defaultSortAsc
                              ? "sorting_asc"
                              : "sorting_desc"
                    ).addClass("kt-datatable__cell--sorted");
                    th.find("> span").append(
                        '<i class="' +
                            (this.config.defaultSortAsc ? "flaticon2-arrow-down" : "flaticon2-arrow-up") +
                            '"></i>'
                    );
                }

                th.data("map", m)
                    .addClass("sorting")
                    .click((e) => {
                        const target = $(e.currentTarget);
                        if (!target.hasClass("kt-datatable__cell--sorted")) {
                            target
                                .siblings(".kt-datatable__cell--sorted")
                                .removeClass("kt-datatable__cell--sorted")
                                .find("> span > i")
                                .remove();
                            target.addClass("kt-datatable__cell--sorted");
                        }

                        let existingIcon = target.find("> span > i");
                        if (existingIcon.length === 0) existingIcon = $("<i />").appendTo(target.find("> span"));

                        if (target.hasClass("sorting_desc")) {
                            target.removeClass("sorting_desc").addClass("sorting_asc");
                            existingIcon.removeClass("flaticon2-arrow-up").addClass("flaticon2-arrow-down");
                        } else if (target.hasClass("sorting_asc")) {
                            target.removeClass("sorting_asc").addClass("sorting_desc");
                            existingIcon.removeClass("flaticon2-arrow-down").addClass("flaticon2-arrow-up");
                        } else {
                            target
                                .siblings(".sorting_desc, .sorting_asc")
                                .removeClass("sorting_desc")
                                .removeClass("sorting_asc");
                            target.addClass("sorting_asc");
                            existingIcon.removeClass("flaticon2-arrow-up").addClass("flaticon2-arrow-down");
                        }

                        this.loadData();
                    });
            }
        }

        if (this.config.autoload) {
            if (this.config.locationDependend === true) {
                if (CurrentLocationService.getLocationId() !== undefined) {
                    this.loadData();
                }
            } else {
                this.loadData();
            }
        }
    }

    public findRowByModel(m: T): JQuery {
        let row: JQuery;

        this.$tBody.find("> .kt-datatable__row").each((i, e) => {
            const s = $(e);
            if (s.data("model")["id"] === m["id"]) {
                row = s;
                return false;
            }
        });

        return row!;
    }

    public listenTo(filter: ITableFilterExtension) {
        filter.onChange = () => this.loadData(true);
    }

    public onClick(event: IRowClickEvent<T>, mouseWheelEvent?: IRowClickEvent<T>) {
        this.clickEvent = event;
        this.mouseWheelClickEvent = mouseWheelEvent;
        this.$table.addClass("clickable");
    }

    public async loadData(resetPaginate?: boolean) {
        if (resetPaginate !== undefined && resetPaginate === true) this.currentPage = 0;

        const filter = this.getFilter();

        const height = this.$tBody.height();
        if (height > 0) this.$tBody.height(height);

        if (this.config.beforeTableClears !== undefined) this.config.beforeTableClears();

        this.$tBody.find("> tr").remove();
        const loader = $(
            `<tr class="kt-datatable__row empty"><td class="kt-datatable__cell" colspan="${this.config.columnMapping.length}"><div class="kt-spinner kt-spinner--lg kt-spinner--brand"></div></td></tr>`
        ).appendTo(this.$tBody);

        if (this.config.useJsHistory) history.replaceState(filter, "datatable");

        this.cancelRunningXhr();

        try {
            const abortController = new AbortController();
            this.abortController = abortController;

            const pagedResult = await this.dataSource.getData(filter, abortController.signal);
            loader.remove();

            this.currentPage = Math.floor(pagedResult.offset / pagedResult.limit);
            this.currentItems = pagedResult.items.length;
            this.hasPrev = pagedResult.hasPrev;
            this.hasNext = pagedResult.hasNext;
            this.setPaginating();

            if (pagedResult.items.length === 0) {
                this.$tBody.append(
                    `<tr class="kt-datatable__row nodata"><td  class="kt-datatable__cell" colspan="${this.config.columnMapping.length}">${this.config.noDatatext !== undefined ? this.config.noDatatext : i18n("general.noItemsFound")}</td>`
                );
            } else {
                for (const i in pagedResult.items) {
                    this.$tBody.append(this.renderRow(pagedResult.items[i], (i as any) % 2 === 0));
                }
            }

            this.$tBody.removeAttr("style");
            if (this.config.dataEvent !== undefined) {
                this.config.dataEvent(pagedResult.items);
            }
        } catch (ex: any) {
            logError(ex);

            // if request has been aborted - do nothing
            if (isRequestAbortedError(ex)) {
                return;
            }

            const ta = ex as DaoError;
            if (ta?.baseContext?.status === 401 || ta?.baseContext?.status === 403) {
                return;
            }
            alert("Could not load data: Reason: " + ta.message);
        }
    }

    private renderRow(model: T, isEven: boolean): JQuery {
        const row = $(`<tr class="kt-datatable__row ${isEven ? "kt-datatable__row--even" : ""}" />`);

        if (this.config.rowClass !== undefined) {
            const rc = this.config.rowClass(model);
            if (rc !== undefined) row.addClass(rc);
        }

        row.data("model", model).click((e) => {
            if (this.clickEvent !== undefined) this.clickEvent($(e.currentTarget).data("model"), e);
        });

        row.mousedown((e) => {
            if (e.which === 2) {
                e.preventDefault();
                e.stopImmediatePropagation();

                return true;
            }
        });

        row.mouseup((e) => {
            if (e.which === 2) {
                if ($(e.target).is("a")) return false;

                e.preventDefault();
                e.stopImmediatePropagation();

                if (this.mouseWheelClickEvent !== undefined) {
                    this.mouseWheelClickEvent($(e.currentTarget).data("model"), e);
                }

                return true;
            }
        });

        for (const i in this.config.columnMapping) {
            const m = this.config.columnMapping[i];
            const value = this.resolvePropertyValue(model!, m.property);

            const td = $(
                `<td class="kt-datatable__cell" ${m.styles !== undefined ? `style="${this.resolveStyles(m.styles)}"` : ""} />`
            ).appendTo(row);

            if (m.hideMobile !== undefined && m.hideMobile === true) {
                td.addClass("kt-visible-desktop-table-cell kt-visible-tablet-table-cell");
            } else if (m.hideTablet !== undefined && m.hideTablet === true) {
                td.addClass("kt-visible-desktop-table-cell");
            }

            if (value === undefined && m.formatter === undefined) continue;

            if (m.formatter === undefined) td.html(Format.htmlEscape(value)!);
            else {
                const fVal = m.formatter(model, value);
                if (typeof fVal === "object") td.append(fVal);
                else td.html(fVal);
            }
        }

        return row;
    }

    public getFilter(): IPaginatedQry {
        let filter: IPaginatedQry = {};

        if (this.config.filterProvider !== undefined) filter = this.config.filterProvider.getFilter();

        if (this.config.paginated) {
            filter.limit = this.config.rowsPerPage;
            filter.offset = filter.limit! * this.currentPage;
        }

        const sorting = this.$tHead.find(".sorting_desc, .sorting_asc");
        if (sorting.length !== 0) {
            const sortableMap = sorting.data("map") as IColumnMapping<T>;
            filter["isAscending"] = sorting.hasClass("sorting_asc");
            filter["orderBy"] = sortableMap.property;
        }

        return filter;
    }

    private setPaginating() {
        if (!this.config.paginated) return;

        this.$pageinate!.empty();
        const activePage = this.currentPage + 1;

        const maxPages = 8;
        const restLeft = this.currentPage - maxPages;

        let hasLeftDotAppended = false;
        const until = this.currentPage * this.config.rowsPerPage! + this.currentItems;

        this.$pageInfo.html(
            `<span class="kt-datatable__pager-detail">${i18n("general.pagination", { from: this.currentPage * this.config.rowsPerPage! + 1, until: until })}</span>`
        );

        const appendPageDelegate = (pageNumber) => {
            const isCurrentPage = activePage === pageNumber;
            const s = $(
                `<li><a class="kt-datatable__pager-link kt-datatable__pager-link-number ${isCurrentPage ? "kt-datatable__pager-link--active" : ""}" data-page="${pageNumber}" title="${pageNumber}">${pageNumber}</a></li>`
            )
                .data("index", pageNumber - 1)
                .appendTo(this.$pageinate!);
            if (!isCurrentPage) {
                s.click((e) => {
                    this.currentPage = $(e.currentTarget).data("index");
                    this.loadData();
                });
            }
        };

        $(
            `<li><a title="${i18n("general.back")}" class="kt-datatable__pager-link kt-datatable__pager-link--prev ${this.hasPrev === false ? "kt-datatable__pager-link--disabled" : ""}" ${this.hasPrev === false ? 'disabled="disabled"' : ""}><i class="flaticon2-back"></i></a></li>`
        )
            .appendTo(this.$pageinate!)
            .click((e) => {
                if ($(e.currentTarget).find("> a").hasClass("kt-datatable__pager-link--disabled")) return false;

                this.currentPage--;
                this.loadData();
            });

        for (let i = 1; i <= activePage; i++) {
            if (i < restLeft) {
                if (!hasLeftDotAppended) {
                    appendPageDelegate(1);

                    $('<li><a class="kt-datatable__pager-link kt-datatable__pager-link-number">. . .</a></li>')
                        .appendTo(this.$pageinate!)
                        .click(() => {
                            this.currentPage -= 10;
                            this.loadData();
                        });

                    hasLeftDotAppended = true;
                }

                continue;
            }

            appendPageDelegate(i);
        }

        $(
            `<li><a title="${i18n("general.forward")}" class="kt-datatable__pager-link kt-datatable__pager-link--next ${this.hasNext === false ? "kt-datatable__pager-link--disabled" : ""}" ${this.hasNext === false ? 'disabled="disabled"' : ""}><i class="flaticon2-next"></i></a></li>`
        )
            .appendTo(this.$pageinate!)
            .click((e) => {
                if ($(e.currentTarget).find("> a").hasClass("kt-datatable__pager-link--disabled")) return false;

                this.currentPage++;
                this.loadData();
            });
    }

    private resolvePropertyValue(model: object, property: string): any {
        const splits = property.split(".");

        for (let i = 0; i < splits.length; i++) {
            model = model[splits[i]];
            if (model === undefined) return undefined;
        }

        return model;
    }

    private resolveStyles(styles: object): string {
        if (styles === undefined) return "";

        let css = "";
        for (const i in styles) css += i + ":" + styles[i] + ";";

        return css;
    }

    private cancelRunningXhr() {
        this.abortController?.abort();
    }
}
